'use client';
import React, { useEffect, useState } from 'react';
import { X, Bell, Heart, MessageSquare, UserPlus, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';

interface NotificationToastProps {
  id: string;
  title: string;
  message: string;
  type?: 'follow_request_accepted' | 'flight_verified' | 'follow_request' | 'like' | 'new_follower' | 'flight_marked_for_review';
  onClose: (id: string) => void;
  onClick?: () => void;
  duration?: number;
}

const NotificationToast: React.FC<NotificationToastProps> = ({
  id,
  title,
  message,
  type = 'default',
  onClose,
  onClick,
  duration = 5000
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  // Get icon based on notification type
  const getIcon = () => {
    switch (type) {
      case 'like':
        return <Heart className="h-4 w-4 text-red-500" fill="currentColor" />;
      case 'comment':
        return <MessageSquare className="h-4 w-4 text-blue-500" />;
      case 'new_follower':
        return <UserPlus className="h-4 w-4 text-green-500" />;
      case 'follow_request':
        return <Mail className="h-4 w-4 text-orange-500" />;
      case 'system':
        return <Bell className="h-4 w-4 text-purple-500" />;
      default:
        return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  // Get color scheme based on type
  const getColorScheme = () => {
    switch (type) {
      case 'like':
        return 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/50';
      case 'comment':
        return 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/50';
      case 'new_follower':
        return 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/50';
      case 'follow_request':
        return 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/50';
      case 'system':
        return 'border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-950/50';
      default:
        return 'border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-950/50';
    }
  };

  useEffect(() => {
    // Animate in
    const showTimer = setTimeout(() => setIsVisible(true), 100);

    // Progress countdown
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev - (100 / (duration / 100));
        return newProgress <= 0 ? 0 : newProgress;
      });
    }, 100);

    // Auto close after duration
    const closeTimer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(closeTimer);
      clearInterval(progressInterval);
    };
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(id);
    }, 300); // Match animation duration
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
      handleClose();
    }
  };

  return (
    <div
      className={cn(
        "max-w-sm w-full transform transition-all duration-300 ease-out cursor-pointer",
        isVisible && !isExiting ? "translate-x-0 opacity-100 scale-100" : "translate-x-full opacity-0 scale-95",
        getColorScheme(),
        "border rounded-lg shadow-lg backdrop-blur-sm relative overflow-hidden"
      )}
      onClick={handleClick}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="flex-shrink-0 mt-0.5">
            {getIcon()}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
              {title}
            </h4>
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 line-clamp-2">
              {message}
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            className="flex-shrink-0 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
          >
            <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700">
        <div 
          className="h-full bg-current transition-all ease-linear"
          style={{
            width: `${progress}%`,
            color: type === 'like' ? '#ef4444' : 
                   type === 'comment' ? '#3b82f6' : 
                   type === 'new_follower' ? '#10b981' : 
                   type === 'follow_request' ? '#f59e0b' : 
                   type === 'system' ? '#8b5cf6' : '#6b7280'
          }}
        />
      </div>
    </div>
  );
};

export default NotificationToast;